"use client";

import { c as _c } from "react/compiler-runtime";
import { Canvas, type CanvasProps } from "@react-three/fiber";
import React, { useCallback, useRef, useState } from "react";
import { useDeferredClientMount } from "#/shared/hooks/useDeferredClientMount";
import { attachWebGLContextRecovery, disposeWebGLRenderer } from "#/shared/lib/webglCanvasRecovery";
type WebGLCanvasShellInnerProps = CanvasProps & {
  onContextLost: () => void;
};
const WebGLCanvasShellInner: React.FC<WebGLCanvasShellInnerProps> = t0 => {
  const $ = _c(11);
  let canvasProps;
  let onContextLost;
  let onCreated;
  if ($[0] !== t0) {
    ({
      onContextLost,
      onCreated,
      ...canvasProps
    } = t0);
    $[0] = t0;
    $[1] = canvasProps;
    $[2] = onContextLost;
    $[3] = onCreated;
  } else {
    canvasProps = $[1];
    onContextLost = $[2];
    onCreated = $[3];
  }
  const disposeRendererRef = useRef(undefined);
  const detachContextRecoveryRef = useRef(undefined);
  let t1;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      detachContextRecoveryRef.current?.();
      detachContextRecoveryRef.current = undefined;
      disposeWebGLRenderer(disposeRendererRef.current);
      disposeRendererRef.current = undefined;
    };
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const onShellCleanup = t1;
  const {
    isReady
  } = useDeferredClientMount(onShellCleanup);
  if (!isReady) {
    return null;
  }
  let t2;
  if ($[5] !== onContextLost || $[6] !== onCreated) {
    t2 = state => {
      const {
        gl
      } = state;
      gl.setClearColor("#000000", 0);
      detachContextRecoveryRef.current?.();
      detachContextRecoveryRef.current = attachWebGLContextRecovery(gl.domElement, onContextLost);
      disposeRendererRef.current = () => {
        gl.dispose();
      };
      onCreated?.(state);
    };
    $[5] = onContextLost;
    $[6] = onCreated;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== canvasProps || $[9] !== t2) {
    t3 = <Canvas {...canvasProps} onCreated={t2} />;
    $[8] = canvasProps;
    $[9] = t2;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  return t3;
};
type WebGLCanvasShellProps = CanvasProps;

/**
 * R3F canvas wrapper for decorative marketing models.
 * Defers mount one frame (avoids off-screen prefetch init), disposes on unmount,
 * and remounts when the browser reports `webglcontextlost`.
 */
export const WebGLCanvasShell: React.FC<WebGLCanvasShellProps> = props => {
  const $ = _c(4);
  const [canvasKey, setCanvasKey] = useState(0);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      window.requestAnimationFrame(() => {
        setCanvasKey(_temp);
      });
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== canvasKey || $[2] !== props) {
    t1 = <WebGLCanvasShellInner key={canvasKey} {...props} onContextLost={t0} />;
    $[1] = canvasKey;
    $[2] = props;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
};
function _temp(previousKey) {
  return previousKey + 1;
}